import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Typography, Space, Statistic, Tag, Tooltip, Progress } from 'antd';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  MarkerType,
  Position,
  ConnectionLineType,
  Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { useTransactionsStore } from '@/store/transactions';
import { useTernaryDarkMode } from 'usehooks-ts';
import { getEndpointScores } from '@/shared/api';
import { getAuthToken } from '@/utils/auth';
import EndpointPerformanceTrend from '@/components/EndpointPerformanceTrend';

const { Title, Text } = Typography;

// Helper function to determine if integration is active based on status
const isIntegrationActive = (status?: string): boolean => {
  return status === 'Active' || status === 'ACTIVE';
};

interface Config {
  _id: string;
  name: string;
  connectorId: string;
  integrationId: string;
  source: {
    type: string;
    url: string;
    [key: string]: any;
  };
  destination: {
    type: string;
    url: string;
    [key: string]: any;
  };
  dataMapping?: any[];
  enrichment?: any;
  status?: string; // Add status field
  [key: string]: any;
}

interface DateFilterValue {
  preset: string;
  startDate: string | null;
  endDate: string | null;
  displayText: string;
}

// Custom Integration Step Node with Modern Design
const IntegrationStepNode = ({ data }: { data: any }) => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  
  const getStepGradient = (stepType: string) => {
    switch (stepType) {
      case 'source': 
        return '#1f6feb';
      case 'mapping': 
        return '#3d4e64';
      case 'enrichment': 
        return '#245b8a';
      case 'destination': 
        return '#0b6b45';
      default: 
        return '#1f6feb';
    }
  };

  const getStepIcon = (stepType: string) => {
    switch (stepType) {
      case 'source': return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
      );
      case 'mapping': return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      );
      case 'enrichment': return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>
        </svg>
      );
      case 'destination': return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
        </svg>
      );
      default: return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
        </svg>
      );
    }
  };

  const stepGradient = getStepGradient(data.stepType);
  const hasError = data.status === 'error';
  const hasWarning = data.status === 'warning';

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '200px',
        padding: '16px',
      }}
    >
      <Handle 
        type="target" 
        position={Position.Left} 
        style={{ 
          background: isDarkMode ? '#4A90E2' : '#2C3E50', 
          width: '10px',
          height: '10px',
          border: '2px solid white',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
        }} 
      />
      
      {/* Main Node Container */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        
        {/* Main Circle Node */}
        <div
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: isDarkMode 
              ? `linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)` 
              : `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
            border: hasError 
              ? '3px solid #ff4d4f' 
              : hasWarning 
                ? '3px solid #faad14' 
                : `3px solid transparent`,
            backgroundClip: 'padding-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.12),
              0 2px 8px rgba(0, 0, 0, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            `,
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
          }}
        >
          {/* Inner gradient circle with icon */}
          <div
            style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: stepGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Animated shimmer effect */}
            <div
              style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
                animation: 'shimmer 3s infinite',
                transform: 'rotate(45deg)',
              }}
            />
            {getStepIcon(data.stepType)}
          </div>
          
          {/* Status indicator */}
          {(hasError || hasWarning) && (
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: hasError 
                  ? 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)' 
                  : 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
                border: '3px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: 'white',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                animation: hasError ? 'errorPulse 1s infinite' : 'warningPulse 2s infinite',
              }}
            >
              {hasError ? '!' : '⚠'}
            </div>
          )}
        </div>

        {/* Label and Metrics Container */}
        <div
          style={{
            marginTop: '16px',
            textAlign: 'center',
            background: isDarkMode 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(0, 0, 0, 0.02)',
            borderRadius: '12px',
            padding: '12px 16px',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
            minWidth: '160px',
          }}
        >
          {/* Step Label */}
          <div 
            style={{ 
              fontWeight: '600', 
              fontSize: '16px', 
              color: isDarkMode ? '#fff' : '#000',
              marginBottom: '4px',
              background: stepGradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {data.label}
          </div>
          
          {/* Step Type */}
          <div 
            style={{ 
              fontSize: '13px', 
              color: isDarkMode ? '#aaa' : '#666',
              marginBottom: '12px',
              fontWeight: '500',
            }}
          >
            {data.type}
          </div>
          
          {/* Metrics */}
          {data.metrics && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '12px' }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                color: isDarkMode ? '#888' : '#666'
              }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1890ff' }}>
                  {data.metrics.calls}
                </div>
                <div>calls/min</div>
              </div>
              {data.metrics.latency && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  color: isDarkMode ? '#888' : '#666'
                }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#52c41a' }}>
                    {data.metrics.latency}
                  </div>
                  <div>ms avg</div>
                </div>
              )}
            </div>
          )}
          
          {/* Error indicator */}
          {data.errors && data.errors > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                display: 'inline-block',
              }}>
                {data.errors} errors/min
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Handle 
        type="source" 
        position={Position.Right} 
        style={{ 
          background: isDarkMode ? '#4A90E2' : '#2C3E50', 
          width: '10px',
          height: '10px',
          border: '2px solid white',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
        }} 
      />
    </div>
  );
};

const nodeTypes = {
  integrationStep: IntegrationStepNode,
};

// Layout function using Dagre for horizontal flow
const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const dagreGraph = new dagre.graphlib.Graph();
  (dagreGraph as any).setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: 'LR', // Left to Right
    ranksep: 250,  // Increased horizontal spacing for better arrow visibility
    nodesep: 100,  // Increased vertical spacing
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 160, height: 140 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id) as any;
    node.targetPosition = Position.Left;
    node.sourcePosition = Position.Right;
    node.position = {
      x: nodeWithPosition.x - 80,
      y: nodeWithPosition.y - 70,
    };
  });

  return { nodes, edges };
};

const IntegrationFlowChart = ({ config, configId }: { config: Config; configId: string }) => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const { transactions } = useTransactionsStore();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView } = useReactFlow();

  // Get transaction count and metrics
  const transactionCount = transactions[configId]?.data?.length || 0;
  const callsPerMinute = Math.floor(transactionCount / 10) || Math.floor(Math.random() * 50) + 10;

  // Generate integration pipeline based on config
  const generateIntegrationFlow = useMemo(() => () => {
    const pipelineNodes: Node[] = [];
    const pipelineEdges: Edge[] = [];
    
    // 1. Source Node
    pipelineNodes.push({
      id: 'source',
      type: 'integrationStep',
      position: { x: 0, y: 0 },
      data: {
        stepType: 'source',
        label: 'Source',
        type: config.source?.type || 'API Endpoint',
        status: isIntegrationActive(config.status) ? 'normal' : 'warning',
        metrics: {
          calls: callsPerMinute,
          latency: Math.floor(Math.random() * 100) + 50,
        },
        errors: isIntegrationActive(config.status) ? 0 : Math.floor(Math.random() * 3),
      },
    });

    // 2. Data Mapping Node (always present)
    pipelineNodes.push({
      id: 'mapping',
      type: 'integrationStep',
      position: { x: 0, y: 0 },
      data: {
        stepType: 'mapping',
        label: 'Data Mapping',
        type: config.dataMapping ? `${config.dataMapping.length} mappings` : 'Field mapping',
        status: 'normal',
        metrics: {
          calls: callsPerMinute,
          latency: Math.floor(Math.random() * 50) + 10,
        },
      },
    });

    // 3. Data Enrichment Node (conditional)
    if (config.enrichment) {
      pipelineNodes.push({
        id: 'enrichment',
        type: 'integrationStep',
        position: { x: 0, y: 0 },
        data: {
          stepType: 'enrichment',
          label: 'Data Enrichment',
          type: 'Data enhancement',
          status: 'normal',
          metrics: {
            calls: callsPerMinute,
            latency: Math.floor(Math.random() * 80) + 20,
          },
        },
      });
    }

    // 4. Destination Node
    pipelineNodes.push({
      id: 'destination',
      type: 'integrationStep',
      position: { x: 0, y: 0 },
      data: {
        stepType: 'destination',
        label: 'Destination',
        type: config.destination?.type || 'Webhook',
        status: 'normal',
        metrics: {
          calls: callsPerMinute,
          latency: Math.floor(Math.random() * 120) + 30,
        },
      },
    });

    // Create edges based on the pipeline
    const nodeIds = pipelineNodes.map(node => node.id);
    
    for (let i = 0; i < nodeIds.length - 1; i++) {
      const sourceId = nodeIds[i];
      const targetId = nodeIds[i + 1];
      const isActive = isIntegrationActive(config.status);
      
      pipelineEdges.push({
        id: `${sourceId}-${targetId}`,
        source: sourceId,
        target: targetId,
        type: 'smoothstep', // Using smoothstep for better curves
        animated: isActive,
        label: `${callsPerMinute} calls/min`,
        labelStyle: {
          fontSize: '12px',
          fontWeight: 'bold',
          fill: isDarkMode ? '#ffffff' : '#000000',
        },
        labelBgStyle: {
          fill: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
          fillOpacity: 1,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 8,
          height: 8,
          color: isActive ? '#1890ff' : '#ff4d4f',
        },
        style: { 
          stroke: isActive ? '#1890ff' : '#ff4d4f',
          strokeWidth: 5, // Increased stroke width for better visibility
          strokeDasharray: isActive ? undefined : '15,10',
        },
      });
    }

    return getLayoutedElements(pipelineNodes, pipelineEdges);
  }, [config, transactionCount, callsPerMinute, isDarkMode]);

  // Initialize and update flow data
  useEffect(() => {
    if (!config?._id) return; // Don't generate if config is not ready
    
    const { nodes: layoutedNodes, edges: layoutedEdges } = generateIntegrationFlow();
    if (layoutedNodes.length > 0) { // Only update if we have nodes
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      
      // Fit view after a short delay
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 800 });
      }, 100);
    }
  }, [generateIntegrationFlow, setNodes, setEdges, fitView, config?._id]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Prevent nodes from disappearing on click
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    // Do nothing - just prevent default behavior
  }, []);

  const onSelectionChange = useCallback(({ nodes, edges }: { nodes: Node[], edges: Edge[] }) => {
    // Do nothing - just prevent default selection behavior that might clear nodes
  }, []);

  return (
    <div style={{ width: '100%', height: '600px', position: 'relative' }}>
      {/* SVG Definitions for Gradients and Animations */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={isDarkMode ? "#4A90E2" : "#2C3E50"} />
            <stop offset="50%" stopColor={isDarkMode ? "#5BA0F2" : "#34495E"} />
            <stop offset="100%" stopColor={isDarkMode ? "#6BB0FF" : "#3C5A78"} />
          </linearGradient>
          <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={isDarkMode ? "#4A90E2" : "#2C3E50"} stopOpacity="0.8" />
            <stop offset="50%" stopColor={isDarkMode ? "#5BA0F2" : "#34495E"} stopOpacity="1" />
            <stop offset="100%" stopColor={isDarkMode ? "#6BB0FF" : "#3C5A78"} stopOpacity="0.8" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              values="0 0; 100 0; 0 0"
              dur="3s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Global CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        
        @keyframes errorPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes warningPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes dataFlow {
          0% { stroke-dashoffset: 20; }
          100% { stroke-dashoffset: 0; }
        }
        
        /* Enhanced ReactFlow Edge Animations */
        .react-flow__edge-path {
          stroke-dasharray: 6 3;
          animation: dataFlow 2s linear infinite;
        }
        
        .react-flow__edge.animated .react-flow__edge-path {
          stroke-dasharray: 6 3;
          animation: dataFlow 2s linear infinite;
        }
        
        /* Modern Controls Styling */
        .react-flow__controls {
          border-radius: 12px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        }
        
        .react-flow__controls-button {
          border-radius: 8px !important;
          border: none !important;
          background: #1f6feb !important;
          color: white !important;
          transition: all 0.2s ease !important;
        }
        
        .react-flow__controls-button:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
        }
        
        /* Modern MiniMap Styling */
        .react-flow__minimap {
          border-radius: 12px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          overflow: hidden !important;
        }
        
        /* Enhanced Background */
        .react-flow__background {
          background-image: radial-gradient(circle, ${isDarkMode ? '#333' : '#e1e5e9'} 1px, transparent 1px) !important;
        }
      `}</style>
      
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onSelectionChange={onSelectionChange}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        connectionLineType={ConnectionLineType.SmoothStep}
        defaultEdgeOptions={{
          type: 'smoothstep',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 12,
            height: 12,
            color: isDarkMode ? '#4A90E2' : '#2C3E50',
          },
          style: {
            strokeWidth: 2,
            stroke: isDarkMode ? '#4A90E2' : '#2C3E50',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
          },
          animated: true,
        }}
      >
        <Controls 
          style={{
            backgroundColor: isDarkMode ? '#1f1f1f' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#434343' : '#d9d9d9'}`,
          }}
        />
        <MiniMap 
          style={{
            backgroundColor: isDarkMode ? '#1f1f1f' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#434343' : '#d9d9d9'}`,
          }}
          nodeColor={(node) => {
            const stepType = node.data?.stepType;
            switch (stepType) {
              case 'source': return '#1f6feb';
              case 'mapping': return '#5c6b80';
              case 'enrichment': return '#245b8a';
              case 'destination': return '#0b6b45';
              default: return '#1f6feb';
            }
          }}
        />
        <Background 
          variant={BackgroundVariant.Dots} 
          gap={20} 
          size={1}
          color={isDarkMode ? '#333' : '#ccc'}
        />
        {/* Integration Health panel - Horizontal layout at bottom */}
        <Panel position="bottom-center">
          <div 
            style={{ 
              minWidth: '800px',
              maxWidth: '1000px',
              background: isDarkMode 
                ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(45, 45, 45, 0.95) 100%)' 
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              padding: '16px 24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {/* Title Section */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: '#1f6feb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <Title level={5} style={{ margin: 0, color: isDarkMode ? '#fff' : '#000', fontWeight: '600' }}>
                  Integration Health
                </Title>
              </div>
              
              {/* Metrics in a horizontal row */}
              <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: '#0b6b45',
                    lineHeight: '1',
                  }}>
                    {config.enrichment ? 4 : 3}
                  </div>
                  <div style={{ fontSize: '11px', color: isDarkMode ? '#888' : '#666', fontWeight: '500' }}>
                    Active Steps
                  </div>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: '#4A90E2',
                    lineHeight: '1',
                  }}>
                    {callsPerMinute}
                  </div>
                  <div style={{ fontSize: '11px', color: isDarkMode ? '#888' : '#666', fontWeight: '500' }}>
                    Calls/min
                  </div>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: '#5c6b80',
                    lineHeight: '1',
                  }}>
                    {Math.floor(Math.random() * 200) + 100}
                  </div>
                  <div style={{ fontSize: '11px', color: isDarkMode ? '#888' : '#666', fontWeight: '500' }}>
                    Latency (ms)
                  </div>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '700', 
                    color: '#245b8a',
                    lineHeight: '1',
                  }}>
                    {transactionCount}
                  </div>
                  <div style={{ fontSize: '11px', color: isDarkMode ? '#888' : '#666', fontWeight: '500' }}>
                    Transactions
                  </div>
                </div>
              </div>
              
              {/* Status badges */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{
                  background: '#0b6b45',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  {config.enrichment ? '4' : '3'} Steps
                </div>
                <div style={{
                  background: isIntegrationActive(config.status)
                    ? '#0b6b45' 
                    : 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  {isIntegrationActive(config.status) ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
};

const IntegrationUniverse: React.FC<{ config: Config; dateFilter?: DateFilterValue }> = ({ config, dateFilter }) => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const { transactions } = useTransactionsStore();
  const [endpointHealth, setEndpointHealth] = useState<{source?: any, destination?: any}>({});
  const [loadingEndpointHealth, setLoadingEndpointHealth] = useState(false);

  // Log dark/light mode when component loads
  useEffect(() => {
    console.log(`🎨 IntegrationUniverse page loaded in ${isDarkMode ? 'DARK' : 'LIGHT'} mode`);
  }, [isDarkMode]);

  // Use the config ID for the transaction store
  const configId = config._id;
  
  // Get transaction count for this config
  const transactionCount = transactions[configId]?.data?.length || 0;

  // Fetch endpoint health metrics
  useEffect(() => {
    const fetchEndpointHealth = async () => {
      if (!configId || !config.source?.url || !config.destination?.url) return;

      setLoadingEndpointHealth(true);
      try {
        const token = getAuthToken();
        if (!token) return;

        const scores = await getEndpointScores(
          token,
          configId,
          config.source.url,
          config.destination.url
        );

        setEndpointHealth({
          source: scores.sourceStartpoint,
          destination: scores.destinationEndpoint
        });
      } catch (error) {
        console.debug('Failed to fetch endpoint health:', error);
      } finally {
        setLoadingEndpointHealth(false);
      }
    };

    fetchEndpointHealth();
  }, [configId, config.source?.url, config.destination?.url]);

  // Helper to get health color
  const getHealthColor = (score?: number) => {
    if (!score) return '#d9d9d9';
    if (score >= 85) return '#52c41a';
    if (score >= 70) return '#1890ff';
    if (score >= 50) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <div className={`integration-studio-content ${isDarkMode ? 'dark' : 'light'}`}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ color: isDarkMode ? '#fff' : '#102033', marginBottom: '8px' }}>
            Integration Universe
          </Title>
          <Text style={{ color: isDarkMode ? '#888' : '#666' }}>
            Visualize your integration pipeline and data flow from source to destination
          </Text>
        </div>

        {/* Integration Overview */}
        <div
          style={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(45, 45, 45, 0.95) 100%)' 
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            padding: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: '#1f6feb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
            </div>
            <Title level={4} style={{ margin: 0, color: isDarkMode ? '#fff' : '#000', fontWeight: '600' }}>
              Integration Overview
            </Title>
          </div>
          
          <Space direction="vertical" size="small">
            <Text><strong>Integration Name:</strong> {config.name}</Text>
            <Text><strong>Source:</strong> {config.source?.type || 'Unknown'} → {config.source?.url || 'Not configured'}</Text>
            <Text><strong>Destination:</strong> {config.destination?.type || 'Unknown'} → {config.destination?.url || 'Not configured'}</Text>
            <Text><strong>Data Mapping:</strong> {config.dataMapping ? `${config.dataMapping.length} field mappings` : 'Not configured'}</Text>
            <Text><strong>Data Enrichment:</strong> {config.enrichment ? 'Enabled' : 'Disabled'}</Text>
            <Text><strong>Transaction Count:</strong> {transactionCount.toLocaleString()}</Text>
            {dateFilter && (
              <Text><strong>Date Range:</strong> {dateFilter.displayText}</Text>
            )}
          </Space>
        </div>

        {/* Pipeline Performance Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <Card size="small">
            <Statistic
              title="Pipeline Status"
              value={isIntegrationActive(config.status) ? 'Active' : 'Inactive'}
              valueStyle={{ color: isIntegrationActive(config.status) ? '#52c41a' : '#faad14' }}
              prefix={isIntegrationActive(config.status) ? '✅' : '⚠️'}
            />
          </Card>
          <Card size="small">
            <Statistic
              title="Data Throughput"
              value={Math.floor(transactionCount / 10) || Math.floor(Math.random() * 50) + 10}
              suffix="/min"
              valueStyle={{ color: '#1890ff' }}
              prefix="🔄"
            />
          </Card>
          <Card size="small">
            <Statistic
              title="Avg Processing Time"
              value={Math.floor(Math.random() * 200) + 100}
              suffix="ms"
              valueStyle={{ color: '#fa8c16' }}
              prefix="⏱️"
            />
          </Card>
          <Card size="small">
            <Statistic
              title="Success Rate"
              value={isIntegrationActive(config.status) ? Math.floor(Math.random() * 10) + 90 : 0}
              suffix="%"
              valueStyle={{ color: isIntegrationActive(config.status) ? '#52c41a' : '#ff4d4f' }}
              prefix={isIntegrationActive(config.status) ? '✅' : '❌'}
            />
          </Card>
          <Card size="small">
            <Statistic
              title="Pipeline Steps"
              value={config.enrichment ? 4 : 3}
              valueStyle={{ color: '#722ed1' }}
              prefix="🔗"
            />
          </Card>
          <Card size="small">
            <Tooltip title={
              endpointHealth.source ? (
                <div>
                  <div><strong>Error Rate:</strong> {endpointHealth.source.errorRate}%</div>
                  <div><strong>Speed:</strong> {endpointHealth.source.avgSpeed}ms/KB</div>
                  <div><strong>Transactions:</strong> {endpointHealth.source.totalTransactions}</div>
                </div>
              ) : 'Loading...'
            }>
              <Statistic
                title="Source Health"
                value={endpointHealth.source?.score || 0}
                suffix="/100"
                valueStyle={{ color: getHealthColor(endpointHealth.source?.score) }}
                prefix="📥"
                loading={loadingEndpointHealth}
              />
            </Tooltip>
          </Card>
          <Card size="small">
            <Tooltip title={
              endpointHealth.destination ? (
                <div>
                  <div><strong>Error Rate:</strong> {endpointHealth.destination.errorRate}%</div>
                  <div><strong>Speed:</strong> {endpointHealth.destination.avgSpeed}ms/KB</div>
                  <div><strong>Transactions:</strong> {endpointHealth.destination.totalTransactions}</div>
                </div>
              ) : 'Loading...'
            }>
              <Statistic
                title="Destination Health"
                value={endpointHealth.destination?.score || 0}
                suffix="/100"
                valueStyle={{ color: getHealthColor(endpointHealth.destination?.score) }}
                prefix="📤"
                loading={loadingEndpointHealth}
              />
            </Tooltip>
          </Card>
        </div>

        {/* Endpoint Performance Trends */}
        <div
          style={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(45, 45, 45, 0.95) 100%)' 
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            padding: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: '#245b8a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
              </svg>
            </div>
            <Title level={4} style={{ margin: 0, color: isDarkMode ? '#fff' : '#000', fontWeight: '600' }}>
              Endpoint Performance Analysis
            </Title>
          </div>
          
          <EndpointPerformanceTrend 
            sourceScore={endpointHealth.source}
            destinationScore={endpointHealth.destination}
            loading={loadingEndpointHealth}
          />
        </div>

        {/* Integration Pipeline Visualization */}
        <div
          style={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(45, 45, 45, 0.95) 100%)' 
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            padding: '24px',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: '#3d4e64',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <Title level={4} style={{ margin: 0, color: isDarkMode ? '#fff' : '#000', fontWeight: '600' }}>
                Integration Pipeline Flow
              </Title>
            </div>
            <Text type="secondary" style={{ fontSize: '14px', fontWeight: '500' }}>
              Real-time data flow visualization
            </Text>
          </div>
          
          <div style={{
            height: '600px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? '#000000' : '#ffffff',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
            overflow: 'hidden',
          }}>
            <ReactFlowProvider>
              <IntegrationFlowChart config={config} configId={configId} />
            </ReactFlowProvider>
          </div>
        </div>

        {/* Pipeline Legend */}
        <div
          style={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(45, 45, 45, 0.95) 100%)' 
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            padding: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: '#245b8a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <Title level={4} style={{ margin: 0, color: isDarkMode ? '#fff' : '#000', fontWeight: '600' }}>
              Pipeline Legend
            </Title>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{
              background: '#1f6feb',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              Source - Data input endpoint
            </div>
            
            <div style={{
              background: '#3d4e64',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              Data Mapping - Field transformations
            </div>
            
            {config.enrichment && (
              <div style={{
                background: '#245b8a',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>
                </svg>
                Data Enrichment - Data enhancement
              </div>
            )}
            
            <div style={{
              background: '#0b6b45',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
              </svg>
              Destination - Data output endpoint
            </div>
          </div>
        </div>
      </Space>
    </div>
  );
};

export default IntegrationUniverse; 